import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Lightning, Cpu, Globe, ArrowsClockwise, Robot, Pulse,
} from '@phosphor-icons/react';
import Footer from '../components/Footer';

const API = process.env.REACT_APP_BACKEND_URL;
const fmtNum = (n) => (Number(n) || 0).toLocaleString();

const AgentActivityPage = () => {
  const [data, setData] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/agent/payments-race`);
      setData(r.data);
    } catch (_e) { /* swallow */ }
  };

  useEffect(() => {
    document.title = 'Payments Race · Live Agent Endpoints · ServerMemoryRFQ';
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, []);

  const endpoints = data?.top_endpoints || [];
  const totalTodayHits = endpoints.reduce((s, e) => s + Number(e.today || 0), 0);
  const totalAllHits   = endpoints.reduce((s, e) => s + Number(e.total || 0), 0);
  const maxToday       = endpoints.reduce((m, e) => Math.max(m, Number(e.today || 0)), 0) || 1;
  const maxTotal       = endpoints.reduce((m, e) => Math.max(m, Number(e.total || 0)), 0) || 1;
  const totalAgents24h = data?.m2m_requests?.unique_agents_24h ?? 0;

  return (
    <div className="min-h-screen bg-[#05050A] text-white">
      <header className="border-b border-white/10 px-4 py-3 flex items-center justify-between">
        <Link to="/" className="font-['JetBrains_Mono'] font-bold text-cyan-300">← ServerMemoryRFQ</Link>
        <div className="flex items-center gap-2 text-xs">
          <span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
          <span className="text-[#10B981] font-['JetBrains_Mono']">LIVE</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Hero */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#A855F7]/15 border border-[#A855F7]/30 text-[#A855F7] text-xs font-['JetBrains_Mono'] mb-3">
            <Robot size={14} weight="fill" /> M2M / Autonomous Agent Commerce
          </div>
          <h1 className="text-3xl sm:text-5xl font-['JetBrains_Mono'] font-bold tracking-tight">
            Payments <span className="text-[#A855F7]">Race</span>
          </h1>
          <p className="text-[#94A3B8] mt-3 text-sm sm:text-base max-w-xl mx-auto">
            Live AI-agent endpoint activity on ServerMemoryRFQ. All hits are tracked on{' '}
            <span className="text-cyan-300">Base</span> · x402 · MCP · USDC.
          </p>
        </div>

        {/* Mini stat strip */}
        <section className="grid grid-cols-3 gap-3 mb-6">
          <MiniStat label="Hits today"      value={fmtNum(totalTodayHits)} accent="#10B981" Icon={Lightning} testid="ms-today" />
          <MiniStat label="Hits all-time"   value={fmtNum(totalAllHits)}   accent="#A855F7" Icon={Cpu}       testid="ms-total" />
          <MiniStat label="Agents 24h"      value={fmtNum(totalAgents24h)} accent="#22D3EE" Icon={Pulse}     testid="ms-agents" />
        </section>

        {/* Top endpoints leaderboard */}
        <section className="bg-[#0B1120] border border-[#1E293B] rounded-xl p-5" data-testid="top-endpoints">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-sm font-['JetBrains_Mono'] uppercase tracking-wider text-[#A855F7] flex items-center gap-2">
              <Lightning size={16} weight="fill" /> Top Agent Endpoints
            </h2>
            <span className="text-[10px] text-[#64748B] font-['JetBrains_Mono']">Today · refresh 6s</span>
          </div>

          {!endpoints.length ? (
            <p className="text-[#64748B] text-xs">Waiting for the first agent hit…</p>
          ) : (
            <ul className="space-y-4">
              {endpoints.map((e, i) => {
                const todayPct = (Number(e.today) / maxToday) * 100;
                const totalPct = (Number(e.total) / maxTotal) * 100;
                return (
                  <li key={e.path} className="text-xs font-mono" data-testid={`ep-${i}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-cyan-300 truncate flex-1 mr-2">{i + 1}. {e.path}</span>
                      <span className="text-white font-bold whitespace-nowrap">{fmtNum(e.today)} <span className="text-[10px] text-[#10B981]">today</span></span>
                    </div>
                    <div className="h-1 bg-[#1E293B] rounded-full overflow-hidden">
                      <div className="h-full bg-[#10B981]" style={{ width: `${todayPct}%` }} />
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-[10px] text-[#94A3B8]">All-time total</span>
                      <span className="text-[10px] text-[#A855F7] font-bold">{fmtNum(e.total)}</span>
                    </div>
                    <div className="h-[2px] bg-[#1E293B] rounded-full overflow-hidden">
                      <div className="h-full bg-[#A855F7]/70" style={{ width: `${totalPct}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* CTA */}
        <section className="bg-[#11141D] border border-cyan-500/30 rounded-xl p-6 text-center mt-6">
          <h2 className="text-xl font-['JetBrains_Mono'] font-bold mb-2">Want your agent on the board?</h2>
          <p className="text-sm text-[#94A3B8] mb-4">
            Discover, price, and buy server memory autonomously. No signup, no API keys. Pay with USDC on Base.
          </p>
          <div className="flex flex-wrap gap-3 justify-center text-xs font-['JetBrains_Mono']">
            <Link to="/agents"      className="bg-cyan-500 hover:bg-cyan-400 text-black px-4 py-2 rounded font-bold">Start checkout →</Link>
            <Link to="/for-agents"  className="border border-[#334155] hover:bg-white/5 px-4 py-2 rounded">Agent docs</Link>
            <a    href="/api/agent/x402/" className="border border-[#A855F7]/40 hover:bg-[#A855F7]/10 text-[#A855F7] px-4 py-2 rounded">x402 endpoint</a>
          </div>
        </section>

        <div className="mt-6 flex items-center justify-between text-[10px] text-[#64748B] font-['JetBrains_Mono']">
          <span><Globe size={12} className="inline -mt-0.5 mr-1" /> Public · auto-refresh 6s</span>
          <button onClick={load} className="hover:text-white flex items-center gap-1" data-testid="refresh">
            <ArrowsClockwise size={12} /> Refresh now
          </button>
        </div>
      </main>

      <Footer />
    </div>
  );
};

const MiniStat = ({ label, value, accent, Icon, testid }) => (
  <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-3" data-testid={testid}>
    <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider font-['JetBrains_Mono']" style={{ color: accent }}>
      <Icon size={10} weight="fill" /> {label}
    </div>
    <p className="text-lg sm:text-xl font-['JetBrains_Mono'] font-bold text-white mt-0.5">{value}</p>
  </div>
);

export default AgentActivityPage;
